import {Button, Input, Modal, Table} from 'antd'
import {CloseOutlined, DeleteOutlined, EditOutlined} from '@ant-design/icons'
import Divider from 'antd/lib/divider'
import * as React from 'react'
import {useEffect, useRef, useState} from 'react'
import {Link, useLocation} from 'react-router-dom'

import {DspBean, ItemResponseDspBeanObject, ListPagination, ListResponseDspBean} from 'src/api/api'
import {filterFunc, getFilterData, sorterFunc, withActionsMediaQuery} from 'src/util/Util'
import {StorageKey, storageReaderFactory, storageWriterFactory} from '../../../util/Storage'

import dspApi from './DspApi'
import Highlighter from 'react-highlight-words'
import './style.css'
import { debounce } from 'lodash'

const Column = Table.Column

// Note: actual storage namespace is chosen per-instance using `miniExchange`,
// mirroring how publishers use a single namespace.
const storageWriter = storageWriterFactory('dsp')
const storageReader = storageReaderFactory('dsp')
interface Props {
    areActionsVertical?: boolean
    miniExchange?: boolean
}

const DspList: React.FC<Props> = ({ areActionsVertical, miniExchange }) => {

    const storageWriter = storageWriterFactory(miniExchange ? 'miniExchange' : 'dsp')
    const storageReader = storageReaderFactory(miniExchange ? 'miniExchange' : 'dsp')

    const location = useLocation<{ fromDspList?: boolean }>()
    const isInitialMount = useRef(true)
    const [data, setData] = useState<DspBean[]>([])
   const [pagination, setPagination] = useState<{
  current: number
  pageSize: number
  total?: number
}>({
  current: 1,
  pageSize: 10,
  total: 0,
})


    const [searchQuery, setSearchQuery] = useState('')

    const loadData = (page: number, pageSize = pagination.pageSize) => {
  dspApi
    .list(miniExchange, pageSize, page, searchQuery)
    .then((listResponseDspBean: ListResponseDspBean) => {


                const array = listResponseDspBean.data as Array<ItemResponseDspBeanObject>
                const dspBeans: DspBean[] = array.map(item => item.data as DspBean)

                if (dspBeans.length == 0 && page > 1) {
                    loadData(page - 1)
                } else {
                    const newPagination: ListPagination = listResponseDspBean.pagination as ListPagination
                    const storedPage = storageReader.read(StorageKey.SearchParams)?.page
                    if (!storedPage || storedPage !== newPagination.currentPage) {
                    storageWriter.write(StorageKey.SearchParams, {
                        page: newPagination.currentPage
                    })
                    }

                    setData(dspBeans)
                    setPagination({
  current: newPagination.currentPage ?? 1,
  pageSize,
  total: newPagination.total,
})

                }
            })
    }

    const remove = (record: DspBean) => {
        Modal.confirm({
            title: `Delete ${record.name}?`,
            content: 'Are you sure? All linked DSP Endpoints will be deleted',
            cancelText: 'NO',
            okText: 'Yes, I\'m sure',
            onOk: () => {
                dspApi.delete(record.id as number).then(() => {
                    let page = pagination.current
                    if (data.length == 1 && pagination.current > 1) {
                        page -= 1
                    }
                    loadData(page)
                })
            }
        })
    }

    let isFiltered;

    useEffect(() => {
        initialLoad()
    }, [miniExchange])

    const initialLoad = () => {
        const isFromDspPages = location.state?.fromDspList === true

        let page = 1

        if (isFromDspPages) {
            let searchParams = storageReader.read(StorageKey.SearchParams)
            page = searchParams?.page || pagination.current
        } else {
            storageWriter.write(StorageKey.SearchParams, { page: 1 })
        }

        loadData(page)
    }

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false
            return
        }
        if (searchQuery === '' && isFiltered) {
            loadData(1)
            isFiltered = false
        } else {
            debounceSearch()
        }
    },[searchQuery]);

    const debounceSearch = React.useMemo(
        () =>
            debounce((value: string) => {
                loadData(1)
            }, 1000),
        []
    )

    return (
        <div>
            <div style={{ display: 'flex' }}>
                <div className="c-l-search-buttons">
                    <Input
                        addonAfter={<CloseOutlined onClick={() => setSearchQuery('')} />}
                        placeholder="Dsp search"
                        value={searchQuery}
                        onChange={e => {
                            setSearchQuery(e.target.value)
                        }}
                    />
                </div>
            </div>
            <Table
                rowKey={'id'}
                size={'middle'}
              pagination={{
  ...pagination,
  showSizeChanger: true,
  onChange: (page, pageSize) => loadData(page, pageSize),
  onShowSizeChange: (_, pageSize) => loadData(1, pageSize),
}}

                dataSource={data}>
                <Column key="id" title="ID" dataIndex="id" />
                <Column key="name" title="Name" dataIndex="name" filterMultiple
                    filters={getFilterData(data, bean => bean.name)}
                    onFilter={filterFunc(bean => bean.name)}
                    sorter={sorterFunc(bean => bean.name)}
                    render={text => searchQuery !== '' ?
                        <Highlighter
                            highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                            searchWords={[searchQuery]}
                            autoEscape
                            textToHighlight={text.toString()} /> :
                        text}
                />
                <Column key="active" title="Active" dataIndex="active"
                    render={(_text, record) => record.active ? 'yes' : 'no'}
                    sorter={sorterFunc(bean => bean.active ? 'yes' : 'no')}
                    filters={getFilterData(data, bean => bean.active)}
                    onFilter={filterFunc(bean => bean.active)}
                />
                <Column align={'center'} key="action" title="Actions" render={(_text: string, record: DspBean) => (
                    <span>
                        <Link to={`/${miniExchange ? 'mini-exchange' : 'dsp'}/${record.id}/edit`}>
                            <Button htmlType="button" title="Edit">
                                <EditOutlined />
                            </Button>
                        </Link>
                        <Divider className="actions-horisontable-divider"
                            type={areActionsVertical ? 'horizontal' : 'vertical'} />
                        <Button
                            onClick={() => remove(record)}
                            type="primary"
                            htmlType="button"
                            title="Delete"
                            danger>
                            <DeleteOutlined />
                        </Button>
                    </span>
                )} />
            </Table>
            <Link to={`${miniExchange ? '/mini-exchange/create' : '/dsp/create'}`}>
                <Button htmlType="button" type="primary">Create new</Button>
            </Link>
        </div>
    )
}

export default withActionsMediaQuery('550px')(DspList)